import JSZip from 'jszip';
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader.js';
import { MTLLoader } from 'three/examples/jsm/loaders/MTLLoader.js';
import { LoadingManager } from 'three';

export const extractObjFromZip = async (zipUrl) => {
    try {
        const response = await fetch(zipUrl);
        if (!response.ok) {
            throw new Error('Failed to fetch ZIP file');
        }
        
        const zipBlob = await response.blob();
        
        const zip = new JSZip();
        let zipContent;
        try {
            zipContent = await zip.loadAsync(zipBlob);
        } catch (zipError) {
            throw new Error('Failed to load ZIP file');
        }
        
        const objFiles = [];
        const mtlFiles = [];
        const textures = {};
        
        for (const [filename, file] of Object.entries(zipContent.files)) {
            if (filename.toLowerCase().endsWith('.obj')) {
                objFiles.push(filename);
            } else if (filename.toLowerCase().endsWith('.mtl')) {
                mtlFiles.push(filename);
            } else if (/\.(jpe?g|png|tga|bmp)$/i.test(filename)) {
                textures[filename] = await file.async('blob');
            }
        }
        
                
        if (objFiles.length === 0) {
            const foundFiles = Object.keys(zipContent.files);
            throw new Error('No OBJ files found in ZIP');
        }
        
        const objFileName = objFiles[0];
        const objContent = await zipContent.file(objFileName).async('text');
        
        let mtlContent = null;
        const objNameWithoutExt = objFileName.replace(/\.obj$/i, '');
        const correspondingMtl = mtlFiles.find(mtl => 
            mtl.toLowerCase().includes(objNameWithoutExt.toLowerCase())
        );
        
        if (correspondingMtl) {
            mtlContent = await zipContent.file(correspondingMtl).async('text');
        }

        return {
            objContent,
            mtlContent,
            objFileName,
            mtlFileName: correspondingMtl,
            textures
        };
    } catch (error) {
        console.error('Error extracting OBJ from ZIP:', error);
        throw error;
    }
};

export const loadObjModel = async (objContent, mtlContent = null, textures = {}) => {
    return new Promise(async (resolve, reject) => {
        try {
            const manager = new LoadingManager();
            manager.setURLModifier((url) => {
                const base = url.split('/').pop();
                const matchedKey = Object.keys(textures).find(k => k.endsWith(base));
                if (matchedKey) {
                    const blobUrl = URL.createObjectURL(textures[matchedKey]);
                    return blobUrl;
                }
                return url;
            });

            const objLoader = new OBJLoader(manager);

            if (mtlContent) {
                const mtlLoader = new MTLLoader(manager);

                const mtlBlobUrl = URL.createObjectURL(new Blob([mtlContent], { type: 'text/plain' }));
                mtlLoader.load(mtlBlobUrl, (materials) => {
                    materials.preload();
                    objLoader.setMaterials(materials);

                    const objBlobUrl = URL.createObjectURL(new Blob([objContent], { type: 'text/plain' }));
                    objLoader.load(objBlobUrl, (object) => {
                        resolve(object);
                    }, undefined, reject);
                }, undefined, reject);
            } else {
                const objBlobUrl = URL.createObjectURL(new Blob([objContent], { type: 'text/plain' }));
                objLoader.load(objBlobUrl, (object) => {
                    resolve(object);
                }, undefined, reject);
            }
        } catch (error) {
            console.error('Error loading OBJ model:', error);
            reject(error);
        }
    });
};

export const loadDirectObjFile = async (objUrl) => {
    return new Promise(async (resolve, reject) => {
        try {
            const response = await fetch(objUrl);
            if (!response.ok) {
                throw new Error('Failed to fetch OBJ file');
            }
            
            const objContent = await response.text();
            
            const baseUrl = objUrl.substring(0, objUrl.lastIndexOf('/') + 1);
            const objFileName = objUrl.substring(objUrl.lastIndexOf('/') + 1);
            const objNameWithoutExt = objFileName.replace(/\.obj$/i, '');
            const mtlUrl = baseUrl + objNameWithoutExt + '.mtl';
            
            let mtlContent = null;
            try {
                const mtlResponse = await fetch(mtlUrl);
                if (mtlResponse.ok) {
                    mtlContent = await mtlResponse.text();  
                }
            } catch (mtlError) {
                console.error('Error loading MTL file:', mtlError);
            }
            
            const loadedScene = await loadObjModel(objContent, mtlContent, {});
            resolve(loadedScene);
            
        } catch (error) {
            console.error('Error loading direct OBJ file:', error);
            reject(error);
        }
    });
};
